/**
 * Turning what the user typed or picked into something an `<img>` can load without tainting
 * the canvas.
 */

/** The proxy endpoint from ADR-0002, served same-origin by our own container. */
export const PROXY_PATH = '/api/fetch-image';

/** Error copy, quoted verbatim from the handoff. */
export const MESSAGES = {
  emptyUrl: 'Paste a picture link first.',
  badUrl: 'That link would not load as a picture. Try another one.',
} as const;

/**
 * Whether a string is a URL we are willing to hand to the proxy.
 *
 * The proxy re-validates independently — this is only to give immediate feedback rather
 * than a round trip for an obviously wrong value.
 */
export function isSupportedUrl(raw: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
}

/**
 * Route a remote picture through our proxy.
 *
 * Loading a remote image directly taints the canvas for most hosts, and the export then
 * throws at the very end — after the user has placed the board. Going through the proxy makes
 * the image same-origin, so the canvas stays clean (ADR-0002).
 */
export function proxiedUrl(raw: string): string {
  return `${PROXY_PATH}?url=${encodeURIComponent(raw)}`;
}

/** Object URLs from file uploads are same-origin already and must not be proxied. */
export const isObjectUrl = (src: string): boolean => src.startsWith('blob:');

/**
 * Load a picture and resolve once its dimensions are known.
 *
 * `crossOrigin = 'anonymous'` is set before `src`, as the handoff requires — with the proxy
 * in front this is same-origin anyway, but it keeps the canvas clean if the proxy is ever
 * bypassed.
 */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';

    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        reject(new Error(MESSAGES.badUrl));
        return;
      }
      resolve(img);
    };
    img.onerror = () => reject(new Error(MESSAGES.badUrl));

    img.src = src;
  });
}
